import { useCallback, useMemo } from 'react';
import { isTextPrimitive } from 'ziphy-web-shared/basic/helpers';
import { denormalizeMultiSelectValueFromMap, getClearValue, getValueType, normalizeMultiSelectValueToMap, } from 'ziphy-web-shared/basic/lib/forms/fields/field.helpers';
export function useMultiSelect({ value, onChange, isInvalid, multi, disabled, allowUnselect, }) {
    const selectedOptions = useMemo(() => normalizeMultiSelectValueToMap(value), [value]);
    const clearValue = useMemo(() => getClearValue(multi, value), []);
    const updateFieldValue = useCallback((newSelected) => {
        onChange(denormalizeMultiSelectValueFromMap(newSelected, multi, value));
    }, [onChange, multi, value]);
    const handleToggleSelect = useCallback((option) => {
        if (disabled || option.disabled)
            return;
        const newSelected = new Map(selectedOptions);
        if (newSelected.has(option.id)) {
            if (!allowUnselect && newSelected.size === 1)
                return;
            newSelected.delete(option.id);
        }
        else {
            if (!multi)
                newSelected.clear();
            newSelected.set(option.id, {
                id: option.id,
                type: getValueType(value),
                extra: option.extraField ? '' : undefined,
            });
        }
        updateFieldValue(newSelected);
    }, [selectedOptions, disabled, multi, allowUnselect, updateFieldValue]);
    const handleExtraFieldChange = useCallback((option, extraValue) => {
        const newSelected = new Map(selectedOptions);
        if (newSelected.has(option.id)) {
            newSelected.set(option.id, Object.assign(Object.assign({}, newSelected.get(option.id)), { extra: extraValue }));
            updateFieldValue(newSelected);
        }
    }, [selectedOptions, updateFieldValue]);
    const handleOtherFieldChange = useCallback((id, newValue) => {
        const newSelected = new Map(selectedOptions);
        const tmp = newSelected.get(id);
        if (!multi)
            newSelected.clear();
        newSelected.set(id, Object.assign(Object.assign({}, tmp), { other: newValue }));
        updateFieldValue(newSelected);
    }, [selectedOptions, updateFieldValue]);
    const getEffectiveOption = (option) => (Object.assign(Object.assign({}, option), { disabled: disabled || option.disabled, invalid: isInvalid || option.invalid }));
    const hasExtraField = (option) => {
        if (!option.extraField)
            return false;
        if (isTextPrimitive(value) || Array.isArray(value)) {
            throw new Error('Extra fields can only be used when the field value is an object.');
        }
        return selectedOptions.has(option.id);
    };
    return {
        selectedOptions,
        clearValue,
        handleToggleSelect,
        handleExtraFieldChange,
        handleOtherFieldChange,
        getEffectiveOption,
        hasExtraField,
    };
}
